/** Link assigned put ↔ assigned call for Analytics(L) wheel-pair assign P/L. */

import { linkWheelPairPutCall, unlinkWheelPairPutCall, loadOwnLedgerTransactionSnapshot } from "./ownLedgerClient";
import { showToast } from "./toast";
import { afterWheelLedgerChange, type WheelLedgerTransaction } from "./wheelActions";

const MODAL_ID = "sfe-wheel-pair-link-modal";

function isAssignedPut(txn: WheelLedgerTransaction): boolean {
  return (
    txn.instrument_type === "option" &&
    txn.action === "buy" &&
    txn.side === "put" &&
    txn.close_reason === "assigned"
  );
}

function isAssignedCall(txn: WheelLedgerTransaction): boolean {
  return (
    txn.instrument_type === "option" &&
    txn.action === "buy" &&
    txn.side === "call" &&
    txn.close_reason === "assigned"
  );
}

function formatAssignLabel(txn: WheelLedgerTransaction): string {
  const date = (txn.trade_date ?? txn.exp_date ?? "").slice(0, 10);
  const strike = txn.strike != null ? `$${Number(txn.strike)}` : "—";
  const qty = Number(txn.quantity) || 0;
  return `${date} ${txn.ticker ?? "—"} ${strike} ×${qty}`;
}

export function openWheelPairLinkModal(
  callTxn: WheelLedgerTransaction,
  options: { onSaved?: () => void | Promise<void> } = {},
): void {
  if (!(callTxn.id > 0) || !isAssignedCall(callTxn)) {
    showToast("Select an assigned call row to link a put.", "error");
    return;
  }

  let overlay = document.getElementById(MODAL_ID);
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = MODAL_ID;
    overlay.className = "sfe-modal-overlay";
    overlay.innerHTML = `
      <div class="sfe-modal sfe-wheel-pair-link-modal" role="dialog" aria-labelledby="sfe-wheel-pair-link-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-wheel-pair-link-title">Link put assign</h3>
          <button type="button" class="sfe-modal-close" data-action="wheel-pair-close" aria-label="Close">✕</button>
        </div>
        <p class="muted small" data-wheel-pair-call-summary></p>
        <form class="sfe-wheel-pair-link-form" data-wheel-pair-form>
          <label class="sfe-modal-field">Assigned put (same ticker)
            <select name="put_id" required data-wheel-pair-select></select>
          </label>
          <div class="sfe-modal-actions">
            <button type="button" class="ghost small" data-action="wheel-pair-close">Cancel</button>
            <button type="submit" class="primary small">Link</button>
          </div>
        </form>
        <div class="sfe-wheel-pair-existing" data-wheel-pair-existing hidden>
          <p class="muted small">Linked puts</p>
          <ul data-wheel-pair-list></ul>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    overlay.addEventListener("click", (ev) => {
      const t = ev.target as HTMLElement;
      if (t.closest("[data-action='wheel-pair-close']") || t === overlay) {
        overlay!.classList.remove("sfe-modal-open");
      }
    });
  }

  const summary = overlay.querySelector<HTMLElement>("[data-wheel-pair-call-summary]");
  const select = overlay.querySelector<HTMLSelectElement>("[data-wheel-pair-select]");
  const existingWrap = overlay.querySelector<HTMLElement>("[data-wheel-pair-existing]");
  const list = overlay.querySelector<HTMLElement>("[data-wheel-pair-list]");
  const form = overlay.querySelector<HTMLFormElement>("[data-wheel-pair-form]");
  if (!summary || !select || !existingWrap || !list || !form) return;

  void (async () => {
    const all = await loadOwnLedgerTransactionSnapshot(true);
    const ticker = (callTxn.ticker ?? "").toUpperCase();
    const puts = all.filter(
      (t) => isAssignedPut(t) && (t.ticker ?? "").toUpperCase() === ticker && t.id != null,
    );
    const linkedPutIds = new Set(
      (callTxn.wheel_pair_linked_ids ?? []).filter((id) => Number.isFinite(id) && id > 0),
    );

    summary.textContent = `Call: ${formatAssignLabel(callTxn)} — Analytics(L) counts assign P/L only when linked.`;

    select.replaceChildren();
    for (const put of puts.sort((a, b) =>
      String(b.trade_date ?? "").localeCompare(String(a.trade_date ?? "")),
    )) {
      if (put.id == null) continue;
      const opt = document.createElement("option");
      opt.value = String(put.id);
      opt.textContent = formatAssignLabel(put);
      if (linkedPutIds.has(put.id)) opt.disabled = true;
      select.appendChild(opt);
    }
    if (!select.options.length) {
      const opt = document.createElement("option");
      opt.value = "";
      opt.textContent = "No assigned puts for this ticker";
      select.appendChild(opt);
      select.disabled = true;
    } else {
      select.disabled = false;
    }

    const linked = puts.filter((p) => p.id != null && linkedPutIds.has(p.id!));
    if (linked.length) {
      existingWrap.hidden = false;
      list.replaceChildren();
      for (const put of linked) {
        const li = document.createElement("li");
        li.textContent = formatAssignLabel(put);
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "ghost small";
        btn.textContent = "Unlink";
        btn.addEventListener("click", () => {
          void (async () => {
            try {
              await unlinkWheelPairPutCall(put.id!, callTxn.id);
              await afterWheelLedgerChange();
              showToast("Wheel pair removed.", "success");
              overlay!.classList.remove("sfe-modal-open");
              await options.onSaved?.();
            } catch (err) {
              showToast(err instanceof Error ? err.message : "Unlink failed", "error");
            }
          })();
        });
        li.appendChild(btn);
        list.appendChild(li);
      }
    } else {
      existingWrap.hidden = true;
      list.replaceChildren();
    }

    form.onsubmit = (ev) => {
      ev.preventDefault();
      const putId = Number(select.value);
      if (!(putId > 0)) return;
      void (async () => {
        try {
          await linkWheelPairPutCall(putId, callTxn.id);
          await afterWheelLedgerChange();
          showToast("Wheel pair linked.", "success");
          overlay!.classList.remove("sfe-modal-open");
          await options.onSaved?.();
        } catch (err) {
          showToast(err instanceof Error ? err.message : "Link failed", "error");
        }
      })();
    };

    overlay.classList.add("sfe-modal-open");
  })();
}
